/**
 * Parse a time value that could be an epoch string, ISO string, or other format
 * Returns a Date object or null if invalid
 * 
 * @param {string|number|null|undefined} timeValue - Time value to parse
 * @returns {Date|null} - Parsed Date or null
 */
export function parseGameTime(timeValue) {
  if (!timeValue) {
    return null;
  }

  // Handle numeric epoch values
  if (typeof timeValue === 'number') {
    const date = new Date(timeValue * 1000); // Assuming epoch seconds
    return isNaN(date.getTime()) ? null : date;
  }

  // Handle string values
  if (typeof timeValue === 'string') {
    const trimmed = timeValue.trim();
    
    if (!trimmed) {
      return null;
    }

    // Try parsing as epoch string (numeric string)
    if (/^\d+$/.test(trimmed)) {
      const epochSeconds = parseInt(trimmed, 10);
      const date = new Date(epochSeconds * 1000);
      return isNaN(date.getTime()) ? null : date;
    }

    // Try parsing as ISO string or other date format
    const date = new Date(trimmed);
    return isNaN(date.getTime()) ? null : date;
  }

  return null;
}

/**
 * Format a timestamp for display
 * 
 * @param {Date|null} date - Date to format
 * @param {string} fallback - Fallback text if date is invalid
 * @returns {string} - Formatted date string
 */
export function formatTimestamp(date, fallback = 'N/A') {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return fallback;
  }

  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Calculate time remaining until a future date
 * 
 * @param {Date|null} endDate - End date
 * @returns {object} - Object with display string and status
 */
export function getTimeRemaining(endDate) {
  if (!endDate || !(endDate instanceof Date) || isNaN(endDate.getTime())) {
    return {
      display: 'Unknown',
      isExpired: false,
      isValid: false,
    };
  }

  const now = new Date();
  const diffMs = endDate.getTime() - now.getTime();

  if (diffMs <= 0) {
    return {
      display: 'Expired',
      isExpired: true,
      isValid: true,
    };
  }

  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  let display = '';
  if (days > 0) {
    display = `${days}d ${hours}h`;
  } else if (hours > 0) {
    display = `${hours}h ${minutes}m`;
  } else {
    display = `${minutes}m`;
  }

  return {
    display,
    isExpired: false,
    isValid: true,
  };
}
